import { Outlet, useLocation } from 'react-router-dom';
import { PublicNav } from './PublicNav';
import { PublicFooter } from './PublicFooter';

/**
 * The public shell.
 *
 * This used to be `bg-white`, which was right when the whole public site was
 * light. Home, Landing and the footer have since moved to the viewer palette,
 * so the white shell stopped being a background and became a BUG: `main` is
 * `flex-1`, so on any page shorter than the viewport it stretched past the last
 * dark section and showed a white band above the (dark) footer. No amount of
 * padding removes that — it is the shell's own ground, showing for exactly as
 * many pixels as the page is short.
 *
 * The ground is dark by default now, and the few pages still written in the
 * light palette opt out below.
 */

/**
 * Pages whose CONTENT is still light-on-white and would be unreadable on the
 * dark ground.
 *
 * A list here rather than a per-page wrapper, because the ground has to be set
 * by whatever paints the full height. A page cannot set it from inside `main`
 * without reintroducing exactly the short-page gap this module exists to close.
 *
 * The legal pages are the long tail of the old light design system (Terms ~45
 * light utility classes, Privacy ~58) and the race report is entirely light and
 * built for printing. Converting all three is real work with real review;
 * letting them keep their own ground costs one array.
 */
const LIGHT_GROUND_PREFIXES = ['/terms', '/privacy'] as const;

export function isLightGround(pathname: string): boolean {
  const path = pathname.toLowerCase();
  if (LIGHT_GROUND_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) {
    return true;
  }
  // Race report: /replay/races/<raceId>/report — printed, so white.
  return path.startsWith('/replay/races/') && path.endsWith('/report');
}

export function PublicLayout() {
  const { pathname } = useLocation();
  const light = isLightGround(pathname);

  return (
    <div
      className={`flex min-h-screen flex-col ${light ? 'bg-white' : 'bg-viewer-deep'}`}
      data-ground={light ? 'light' : 'dark'}
    >
      <PublicNav light={light} />
      <main className="flex-1">
        <Outlet />
      </main>
      <PublicFooter />
    </div>
  );
}
